import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCircleCheck,
  faTriangleExclamation,
  faCloudArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import { useSaveStatus, type SaveStatus } from '../bidSaveStatus';
import { SAVE_DEBOUNCE_MS } from '../bidStore';
import './SaveStatusIndicator.css';

/** The debounce cadence, in whole seconds, for the resting "Autosaves every Ns" hint. */
const AUTOSAVE_SECONDS = Math.round(SAVE_DEBOUNCE_MS / 1000);

/**
 * Live autosave indicator for the bid editor, shown beside the Bid Details tab.
 *
 * The estimator saves on a debounce through a serial per-bid queue (see `bidStore`); this reflects
 * that queue's state so the user always knows their work is safe:
 *   - idle    → a cloud icon + "Autosaves every Ns"  (resting hint before any edit)
 *   - pending → "Unsaved changes"                     (edits made, debounce not yet elapsed)
 *   - saving  → a SPINNER + "Saving…"                 (shown every time a write starts)
 *   - saved   → "Saved"                               (last write succeeded)
 *   - error   → "Couldn't save"                       (the store also toasts + resyncs)
 */
const CONFIG: Record<
  SaveStatus,
  { icon: typeof faSpinner; spin?: boolean; label: string; className: string }
> = {
  idle: { icon: faCloudArrowUp, label: `Autosaves every ${AUTOSAVE_SECONDS}s`, className: 'is-idle' },
  pending: { icon: faCloudArrowUp, label: 'Unsaved changes', className: 'is-pending' },
  saving: { icon: faSpinner, spin: true, label: 'Saving…', className: 'is-saving' },
  saved: { icon: faCircleCheck, label: 'Saved', className: 'is-saved' },
  error: { icon: faTriangleExclamation, label: "Couldn't save", className: 'is-error' },
};

const SaveStatusIndicator = (): JSX.Element => {
  const { status } = useSaveStatus();
  const cfg = CONFIG[status];
  return (
    <div
      className={`bid-save-indicator ${cfg.className}`}
      // Announce changes politely — an estimator relying on a screen reader still
      // hears "Saving…" / "Saved" without it stealing focus.
      role="status"
      aria-live="polite"
      title={cfg.label}
    >
      <FontAwesomeIcon icon={cfg.icon} spin={cfg.spin} className="bid-save-indicator-icon" />
      <span className="bid-save-indicator-label">{cfg.label}</span>
    </div>
  );
};

export default SaveStatusIndicator;
